import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Waves, Fish, MapPin, Star, Camera, Calendar, Clock, 
  Thermometer, Gauge, Ruler, Weight, Shirt, Upload, Loader2,
  ChevronRight, X, User, Building2, Eye
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import { getImageUrl } from "@/utils/imageUtils";
import SpeciesDetailModal from "@/components/marine/SpeciesDetailModal";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Size options for equipment
const SIZES = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"];
const BCD_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];
const GAS_TYPES = ["Air", "Nitrox"];
const CYLINDER_TYPES = ["Steel", "Aluminum"];
const SUIT_TYPES = ["None", "Shorty", "Full 3mm", "Full 5mm", "Full 7mm", "Semi-Dry", "Dry Suit"];
const BCD_TYPES = ["Jacket", "Wing"];
const WEIGHT_TYPES = ["Belt", "Integrated"];
const CURRENT_OPTIONS = ["None", "Small-", "Small", "Small+", "Medium-", "Medium", "Medium+", "Strong-", "Strong", "Strong+"];
const WAVE_OPTIONS = ["Calm", "Small", "Medium", "Big", "Very Big"];
const SURGE_OPTIONS = ["None", "Light", "Moderate", "Strong", "Very Strong"];

const DiveReportForm = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dive");
  
  // Dive Sites Data
  const [countries, setCountries] = useState([]);
  const [diveSites, setDiveSites] = useState([]);
  const [filteredDiveSites, setFilteredDiveSites] = useState([]);
  
  // Marine Life Suggestions - from dive sites
  const [diveSiteSuggestions, setDiveSiteSuggestions] = useState([]);
  // Marine Life Suggestions - from marine database
  const [databaseSuggestions, setDatabaseSuggestions] = useState([]);
  const [marineLifeInput, setMarineLifeInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [siteSuggestionThumbnails, setSiteSuggestionThumbnails] = useState({});
  const [allSiteSpecies, setAllSiteSpecies] = useState([]); // All species for the dive site (shown on focus)
  const [previewSpecies, setPreviewSpecies] = useState(null); // Species detail modal
  
  // Unit preferences
  const [depthUnit, setDepthUnit] = useState("m");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [tempUnit, setTempUnit] = useState("C");
  const [visibilityUnit, setVisibilityUnit] = useState("m");
  
  // Dive Center & Buddy autocomplete
  const [diveCenters, setDiveCenters] = useState([]);
  const [diveCenterInput, setDiveCenterInput] = useState("");
  const [diveCenterSuggestions, setDiveCenterSuggestions] = useState([]);
  const [showDiveCenterSuggestions, setShowDiveCenterSuggestions] = useState(false);
  
  const [members, setMembers] = useState([]);
  const [buddyInput, setBuddyInput] = useState("");
  const [buddySuggestions, setBuddySuggestions] = useState([]);
  const [showBuddySuggestions, setShowBuddySuggestions] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    // Tab 1: Dive
    title: "",
    dive_date: new Date().toISOString().split('T')[0],
    depth: "",
    dive_time: "",
    temperature: {
      outside: "",
      surface: "",
      underwater: ""
    },
    equipment: {
      gas_type: "",
      nitrox_percentage: "",
      cylinder_size: "",
      cylinder_type: "",
      starting_pressure: "",
      ending_pressure: "",
      suit_type: "",
      suit_thickness: "",
      suit_size: "",
      bcd_type: "",
      bcd_size: "",
      hood: false,
      hood_size: "",
      boots: false,
      boots_size: "",
      weights: "",
      weight_type: ""
    },
    dive_profile_photo: null,
    
    // Tab 2: Site
    country: "",
    dive_site_id: "",
    is_custom_site: false,
    custom_site_name: "",
    custom_coordinates: { lat: "", lng: "" },
    conditions: {
      current: "",
      waves: "",
      surge: "",
      visibility: ""
    },
    marine_life_observations: [],
    rating: 0,
    site_photos: [],
    
    // Species ID
    species_id_requested: false,
    species_id_photo_urls: [],
    
    // Tab 3: Notes
    notes: "",
    dive_center_id: "",
    dive_center_name: "",
    buddies: []  // Array of {user_id, user_name}
  });

  // Check auth and load initial data
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "null");
    if (!currentUser) {
      toast.error("Please login to submit a dive report");
      navigate('/account');
      return;
    }
    setUser(currentUser);
    
    // Fetch dive sites with country info
    fetchDiveSites();
    
    // Fetch dive centers for autocomplete
    fetchDiveCenters();
    
    // Fetch members for buddy autocomplete
    fetchMembers();
  }, [navigate]);

  const fetchDiveSites = async () => {
    try {
      const response = await axios.get(`${API}/map/dive-sites/with-country`);
      const sites = response.data;
      setDiveSites(sites);
      
      // Extract unique countries
      const uniqueCountries = [...new Set(sites.map(s => s.country).filter(Boolean))].sort();
      setCountries(uniqueCountries);
    } catch (error) {
      console.error("Error loading dive sites:", error);
      // Fallback to regular dive sites
      try {
        const fallback = await axios.get(`${API}/map/dive-sites`);
        setDiveSites(fallback.data);
        // Try to extract countries from dive sites
        const uniqueCountries = [...new Set(fallback.data.map(s => s.country).filter(Boolean))].sort();
        setCountries(uniqueCountries);
      } catch (e) {
        toast.error("Failed to load dive sites");
      }
    }
  };

  const fetchDiveCenters = async () => {
    try {
      // Use map/dive-centers which has all dive centers
      const response = await axios.get(`${API}/map/dive-centers`);
      setDiveCenters(response.data);
    } catch (error) {
      console.error("Error loading dive centers:", error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/users/public`);
      setMembers(response.data);
    } catch (error) {
      console.error("Error loading members:", error);
    }
  };

  // Filter dive sites when country changes
  useEffect(() => {
    if (formData.country && formData.country !== "other") {
      const filtered = diveSites.filter(s => s.country === formData.country);
      setFilteredDiveSites(filtered);
    } else {
      setFilteredDiveSites([]);
    }
  }, [formData.country, diveSites]);

  // Filter dive center suggestions
  useEffect(() => {
    if (diveCenterInput.length >= 2) {
      const filtered = diveCenters.filter(dc => 
        dc.name?.toLowerCase().includes(diveCenterInput.toLowerCase())
      ).slice(0, 10);
      setDiveCenterSuggestions(filtered);
    } else {
      setDiveCenterSuggestions([]);
    }
  }, [diveCenterInput, diveCenters]);

  // Filter buddy suggestions
  useEffect(() => {
    if (buddyInput.length >= 2) {
      const filtered = members.filter(m => 
        (m.name?.toLowerCase().includes(buddyInput.toLowerCase()) ||
         m.username?.toLowerCase().includes(buddyInput.toLowerCase())) &&
        !formData.buddies.some(b => b.user_id === m.id)  // Exclude already added
      ).slice(0, 10);
      setBuddySuggestions(filtered);
    } else {
      setBuddySuggestions([]);
    }
  }, [buddyInput, members, formData.buddies]);

  // Load all popular species for the selected dive site (shown on focus before typing)
  useEffect(() => {
    const loadSiteSpecies = async () => {
      if (formData.dive_site_id && !formData.is_custom_site) {
        try {
          // Get dive site's marine_life list
          const siteRes = await axios.get(`${API}/map/dive-sites/${formData.dive_site_id}`);
          const siteMarineLife = siteRes.data?.marine_life || [];
          
          // Try to get frequency data from reports
          let freqMap = {};
          try {
            const freqRes = await axios.get(`${API}/dive-sites/${formData.dive_site_id}/marine-life-suggestions`);
            for (const s of (freqRes.data?.suggestions || [])) {
              freqMap[s.name] = s.frequency;
            }
          } catch {}
          
          // Build sorted list: species with report frequency first, then alphabetical
          const suggestions = siteMarineLife.map(name => ({
            name,
            frequency: freqMap[name] || 0
          })).sort((a, b) => b.frequency - a.frequency || a.name.localeCompare(b.name));
          
          setAllSiteSpecies(suggestions);
          
          // Batch fetch thumbnails for all site species
          const names = suggestions.map(s => s.name);
          if (names.length > 0) {
            try {
              const thumbRes = await axios.post(`${API}/marine-database/batch-thumbnails`, names);
              setSiteSuggestionThumbnails(prev => ({ ...prev, ...thumbRes.data }));
            } catch {}
          }
        } catch {
          setAllSiteSpecies([]);
        }
      } else {
        setAllSiteSpecies([]);
      }
    };
    loadSiteSpecies();
  }, [formData.dive_site_id, formData.is_custom_site]);

  // Fetch marine life suggestions from selected dive site + marine database
  const fetchMarineLifeSuggestions = useCallback(async (query, diveSiteId) => {
    // When no query, show all site species (already loaded) and no DB results
    if (!query || query.length < 2) {
      // diveSiteSuggestions will be derived from allSiteSpecies in render
      setDiveSiteSuggestions([]);
      setDatabaseSuggestions([]);
      return;
    }
    
    try {
      let filteredSiteNames = [];
      // Filter site species by query
      if (diveSiteId && !formData.is_custom_site) {
        const queryLower = query.toLowerCase();
        filteredSiteNames = allSiteSpecies
          .filter(s => s.name.toLowerCase().includes(queryLower))
          .map(s => s.name);
        setDiveSiteSuggestions(filteredSiteNames);
      } else {
        setDiveSiteSuggestions([]);
      }
      
      // Get suggestions from marine database (only when typing)
      try {
        const dbResponse = await axios.get(`${API}/marine-database/search?q=${encodeURIComponent(query)}`);
        const dbSpecies = dbResponse.data.map(s => ({
          id: s.id,
          name: s.common_names?.[0] || s.scientific_name,
          scientific_name: s.scientific_name,
          thumbnail: s.images?.[0] || null,
        }));
        // Filter out any whose name is already in dive site suggestions
        const diveSiteSet = new Set(filteredSiteNames);
        const filteredDb = dbSpecies.filter(s => !diveSiteSet.has(s.name));
        setDatabaseSuggestions(filteredDb);
      } catch (dbError) {
        console.error("Error fetching database suggestions:", dbError);
        setDatabaseSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setDiveSiteSuggestions([]);
      setDatabaseSuggestions([]);
    }
  }, [formData.is_custom_site, allSiteSpecies]);

  // Debounce marine life search - includes dive site ID
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMarineLifeSuggestions(marineLifeInput, formData.dive_site_id);
    }, 300);
    return () => clearTimeout(timer);
  }, [marineLifeInput, formData.dive_site_id, fetchMarineLifeSuggestions]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const addMarineLifeObservation = (observation) => {
    if (observation && !formData.marine_life_observations.includes(observation)) {
      setFormData(prev => ({
        ...prev,
        marine_life_observations: [...prev.marine_life_observations, observation]
      }));
    }
    setMarineLifeInput("");
    setShowSuggestions(false);
  };

  const removeMarineLifeObservation = (observation) => {
    setFormData(prev => ({
      ...prev,
      marine_life_observations: prev.marine_life_observations.filter(o => o !== observation)
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  // Select dive center from autocomplete
  const selectDiveCenter = (diveCenter) => {
    setFormData(prev => ({
      ...prev,
      dive_center_id: diveCenter.id,
      dive_center_name: diveCenter.name
    }));
    setDiveCenterInput(diveCenter.name);
    setShowDiveCenterSuggestions(false);
  };

  // Add buddy from autocomplete
  const addBuddy = (member) => {
    if (!formData.buddies.some(b => b.user_id === member.id)) {
      setFormData(prev => ({
        ...prev,
        buddies: [...prev.buddies, { user_id: member.id, user_name: member.name || member.username }]
      }));
    }
    setBuddyInput("");
    setShowBuddySuggestions(false);
  };

  // Remove buddy
  const removeBuddy = (userId) => {
    setFormData(prev => ({
      ...prev,
      buddies: prev.buddies.filter(b => b.user_id !== userId)
    }));
  };

  // Photo upload for dive profile
  const handleDivePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/upload/report-image`, formDataUpload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, dive_profile_photo: response.data.image_url }));
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    }
  };

  // Site photos upload
  const handleSitePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const token = localStorage.getItem("token");
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload/report-image`, formDataUpload, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        setFormData(prev => ({
          ...prev,
          site_photos: [...prev.site_photos, { url: response.data.image_url, hide_from_gallery: false }]
        }));
      } catch (error) {
        console.error("Error uploading photo:", error);
      }
    }
    toast.success("Photos uploaded!");
  };

  const removeSitePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      site_photos: prev.site_photos.filter((_, i) => i !== index),
      species_id_photo_urls: prev.species_id_photo_urls.filter(url => url !== prev.site_photos[index]?.url)
    }));
  };

  const togglePhotoGalleryVisibility = (index) => {
    setFormData(prev => ({
      ...prev,
      site_photos: prev.site_photos.map((photo, i) => 
        i === index ? { ...photo, hide_from_gallery: !photo.hide_from_gallery } : photo
      )
    }));
  };

  const toggleSpeciesIdPhoto = (photoUrl) => {
    setFormData(prev => {
      const isSelected = prev.species_id_photo_urls.includes(photoUrl);
      return {
        ...prev,
        species_id_photo_urls: isSelected 
          ? prev.species_id_photo_urls.filter(url => url !== photoUrl)
          : [...prev.species_id_photo_urls, photoUrl]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Please enter a title for your dive report");
      setActiveTab("dive");
      return;
    }
    
    if (!formData.dive_date) {
      toast.error("Please select a dive date");
      setActiveTab("dive");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // Build payload
      const payload = {
        title: formData.title,
        dive_date: new Date(formData.dive_date).toISOString(),
        depth: formData.depth ? parseFloat(formData.depth) : null,
        depth_unit: depthUnit,
        dive_time: formData.dive_time ? parseInt(formData.dive_time) : null,
        temperature: {
          outside: formData.temperature.outside ? parseFloat(formData.temperature.outside) : null,
          surface: formData.temperature.surface ? parseFloat(formData.temperature.surface) : null,
          underwater: formData.temperature.underwater ? parseFloat(formData.temperature.underwater) : null,
          unit: tempUnit
        },
        equipment: {
          gas_type: formData.equipment.gas_type || null,
          nitrox_percentage: formData.equipment.gas_type === "Nitrox" && formData.equipment.nitrox_percentage 
            ? parseInt(formData.equipment.nitrox_percentage) : null,
          cylinder_size: formData.equipment.cylinder_size || null,
          cylinder_type: formData.equipment.cylinder_type || null,
          starting_pressure: formData.equipment.starting_pressure ? parseInt(formData.equipment.starting_pressure) : null,
          ending_pressure: formData.equipment.ending_pressure ? parseInt(formData.equipment.ending_pressure) : null,
          suit_type: formData.equipment.suit_type || null,
          suit_thickness: formData.equipment.suit_thickness ? parseInt(formData.equipment.suit_thickness) : null,
          suit_size: formData.equipment.suit_size || null,
          bcd_type: formData.equipment.bcd_type || null,
          bcd_size: formData.equipment.bcd_size || null,
          hood: formData.equipment.hood || false,
          hood_size: formData.equipment.hood_size || null,
          boots: formData.equipment.boots || false,
          boots_size: formData.equipment.boots_size || null,
          weights: formData.equipment.weights ? parseFloat(formData.equipment.weights) : null,
          weight_type: formData.equipment.weight_type || null,
          weights_unit: weightUnit
        },
        dive_profile_photo: formData.dive_profile_photo,
        country: formData.country && formData.country !== "other" ? formData.country : null,
        dive_site_id: !formData.is_custom_site && formData.dive_site_id ? formData.dive_site_id : null,
        dive_site_name: formData.is_custom_site ? formData.custom_site_name : null,
        is_custom_site: formData.is_custom_site,
        custom_coordinates: formData.is_custom_site && formData.custom_coordinates.lat && formData.custom_coordinates.lng
          ? { lat: parseFloat(formData.custom_coordinates.lat), lng: parseFloat(formData.custom_coordinates.lng) }
          : null,
        conditions: {
          current: formData.conditions.current || null,
          waves: formData.conditions.waves || null,
          surge: formData.conditions.surge || null,
          visibility: formData.conditions.visibility ? parseFloat(formData.conditions.visibility) : null,
          visibility_unit: visibilityUnit
        },
        marine_life_observations: formData.marine_life_observations,
        rating: formData.rating || null,
        site_photos: formData.site_photos,
        species_id_requested: formData.species_id_requested,
        species_id_photo_urls: formData.species_id_requested ? formData.species_id_photo_urls : [],
        // Tab 3: Notes
        notes: formData.notes || null,
        dive_center_id: formData.dive_center_id || null,
        dive_center_name: formData.dive_center_name || null,
        buddies: formData.buddies
      };
      
      await axios.post(`${API}/dive-reports`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Dive report submitted successfully!");
      
      // Redirect to profile
      setTimeout(() => {
        navigate(`/profile/${user.id}`);
      }, 1500);
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error(error.response?.data?.detail || "Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section - Profile Style */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Dive Report
          </h1>
          <p className="text-lg text-white/90">
            Record your dive details and share your underwater discoveries
          </p>
        </div>
      </section>

      {/* Form Content */}
      <section className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="dive" className="text-lg py-3">
                  <Gauge className="w-5 h-5 mr-2" />
                  Dive
                </TabsTrigger>
                <TabsTrigger value="site" className="text-lg py-3">
                  <MapPin className="w-5 h-5 mr-2" />
                  Site
                </TabsTrigger>
                <TabsTrigger value="notes" className="text-lg py-3">
                  <Calendar className="w-5 h-5 mr-2" />
                  Notes
                </TabsTrigger>
              </TabsList>

              {/* TAB 1: DIVE */}
              <TabsContent value="dive" className="space-y-6">
                {/* Title & Date */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                      Basic Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => handleInputChange("title", e.target.value)}
                          placeholder="e.g., Morning dive at Coral Garden"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dive_date">Date *</Label>
                        <Input
                          id="dive_date"
                          type="date"
                          value={formData.dive_date}
                          onChange={(e) => handleInputChange("dive_date", e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Depth, Time, Temperature */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Ruler className="w-5 h-5 text-cyan-600" />
                      Dive Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="depth">Max Depth</Label>
                        <div className="flex gap-2">
                          <Input
                            id="depth"
                            type="number"
                            step="0.1"
                            value={formData.depth}
                            onChange={(e) => handleInputChange("depth", e.target.value)}
                            placeholder="0"
                            className="flex-1"
                          />
                          <Select value={depthUnit} onValueChange={setDepthUnit}>
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="m">m</SelectItem>
                              <SelectItem value="ft">ft</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dive_time">Dive Time (minutes)</Label>
                        <Input
                          id="dive_time"
                          type="number"
                          value={formData.dive_time}
                          onChange={(e) => handleInputChange("dive_time", e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Temperature */}
                    <div>
                      <Label className="flex items-center gap-2 mb-3">
                        <Thermometer className="w-4 h-4 text-cyan-600" />
                        Temperature
                        <Select value={tempUnit} onValueChange={setTempUnit}>
                          <SelectTrigger className="w-16 h-7 ml-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="C">°C</SelectItem>
                            <SelectItem value="F">°F</SelectItem>
                          </SelectContent>
                        </Select>
                      </Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Outside</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={formData.temperature.outside}
                            onChange={(e) => handleNestedChange("temperature", "outside", e.target.value)}
                            placeholder="Air"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Surface</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={formData.temperature.surface}
                            onChange={(e) => handleNestedChange("temperature", "surface", e.target.value)}
                            placeholder="Surface"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Underwater</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={formData.temperature.underwater}
                            onChange={(e) => handleNestedChange("temperature", "underwater", e.target.value)}
                            placeholder="Bottom"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Equipment */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shirt className="w-5 h-5 text-cyan-600" />
                      Equipment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Gas & Cylinder */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Gas Type</Label>
                        <div className="flex gap-2">
                          <Select 
                            value={formData.equipment.gas_type} 
                            onValueChange={(v) => {
                              handleNestedChange("equipment", "gas_type", v);
                              if (v !== "Nitrox") {
                                handleNestedChange("equipment", "nitrox_percentage", "");
                              }
                            }}
                          >
                            <SelectTrigger className={formData.equipment.gas_type === "Nitrox" ? "w-24" : "w-full"}>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {GAS_TYPES.map(gas => (
                                <SelectItem key={gas} value={gas}>{gas}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formData.equipment.gas_type === "Nitrox" && (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="21"
                                max="100"
                                value={formData.equipment.nitrox_percentage}
                                onChange={(e) => handleNestedChange("equipment", "nitrox_percentage", e.target.value)}
                                placeholder="32"
                                className="w-16"
                              />
                              <span className="text-sm text-gray-500">%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Cylinder Size</Label>
                        <Input
                          value={formData.equipment.cylinder_size}
                          onChange={(e) => handleNestedChange("equipment", "cylinder_size", e.target.value)}
                          placeholder="e.g., 12L or 80cuft"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cylinder Type</Label>
                        <Select 
                          value={formData.equipment.cylinder_type}
                          onValueChange={(v) => handleNestedChange("equipment", "cylinder_type", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CYLINDER_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Air Consumption */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Starting Pressure (bar)</Label>
                        <Input
                          type="number"
                          value={formData.equipment.starting_pressure}
                          onChange={(e) => handleNestedChange("equipment", "starting_pressure", e.target.value)}
                          placeholder="200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ending Pressure (bar)</Label>
                        <Input
                          type="number"
                          value={formData.equipment.ending_pressure}
                          onChange={(e) => handleNestedChange("equipment", "ending_pressure", e.target.value)}
                          placeholder="50"
                        />
                      </div>
                    </div>

                    {/* Suit */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Suit Type</Label>
                        <Select 
                          value={formData.equipment.suit_type}
                          onValueChange={(v) => handleNestedChange("equipment", "suit_type", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {SUIT_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Thickness (mm)</Label>
                        <Input
                          type="number"
                          value={formData.equipment.suit_thickness}
                          onChange={(e) => handleNestedChange("equipment", "suit_thickness", e.target.value)}
                          placeholder="3"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Suit Size</Label>
                        <Select 
                          value={formData.equipment.suit_size}
                          onValueChange={(v) => handleNestedChange("equipment", "suit_size", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {SIZES.map(size => (
                              <SelectItem key={size} value={size}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* BCD */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>BCD Type</Label>
                        <Select 
                          value={formData.equipment.bcd_type}
                          onValueChange={(v) => handleNestedChange("equipment", "bcd_type", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {BCD_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>BCD Size</Label>
                        <Select 
                          value={formData.equipment.bcd_size}
                          onValueChange={(v) => handleNestedChange("equipment", "bcd_size", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {BCD_SIZES.map(size => (
                              <SelectItem key={size} value={size}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Hood & Boots */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={formData.equipment.hood}
                            onCheckedChange={(v) => handleNestedChange("equipment", "hood", v)}
                          />
                          <Label>Hood</Label>
                        </div>
                        {formData.equipment.hood && (
                          <Select 
                            value={formData.equipment.hood_size}
                            onValueChange={(v) => handleNestedChange("equipment", "hood_size", v)}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {SIZES.map(size => (
                                <SelectItem key={size} value={size}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={formData.equipment.boots}
                            onCheckedChange={(v) => handleNestedChange("equipment", "boots", v)}
                          />
                          <Label>Boots</Label>
                        </div>
                        {formData.equipment.boots && (
                          <Select 
                            value={formData.equipment.boots_size}
                            onValueChange={(v) => handleNestedChange("equipment", "boots_size", v)}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {SIZES.map(size => (
                                <SelectItem key={size} value={size}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Weights */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Weight className="w-4 h-4 text-cyan-600" />
                        Weights
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.5"
                          value={formData.equipment.weights}
                          onChange={(e) => handleNestedChange("equipment", "weights", e.target.value)}
                          placeholder="0"
                          className="flex-1"
                        />
                        <Select value={weightUnit} onValueChange={setWeightUnit}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="lbs">lbs</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select 
                          value={formData.equipment.weight_type}
                          onValueChange={(v) => handleNestedChange("equipment", "weight_type", v)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {WEIGHT_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dive Profile Photo */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Camera className="w-5 h-5 text-cyan-600" />
                      Dive Profile Photo
                    </CardTitle>
                    <CardDescription>
                      Upload a personal photo from this dive
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {formData.dive_profile_photo ? (
                      <div className="relative w-48">
                        <img 
                          src={`${BACKEND_URL}${formData.dive_profile_photo}`}
                          alt="Dive profile"
                          className="w-48 h-48 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleInputChange("dive_profile_photo", null)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500">Upload Photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleDivePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </CardContent>
                </Card>

                {/* Next Tab Button */}
                <div className="flex justify-end">
                  <Button 
                    type="button" 
                    onClick={() => setActiveTab("site")}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Continue to Site Info
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB 2: SITE */}
              <TabsContent value="site" className="space-y-6">
                {/* Date Display */}
                <Card className="bg-cyan-50 border-cyan-200">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                      <div>
                        <span className="text-sm text-gray-600">Dive Date:</span>
                        <span className="ml-2 font-medium">
                          {formData.dive_date ? new Date(formData.dive_date).toLocaleDateString('en-US', {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                          }) : 'Not set'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Location */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-cyan-600" />
                      Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Select 
                          value={formData.country}
                          onValueChange={(v) => {
                            handleInputChange("country", v);
                            handleInputChange("dive_site_id", "");
                            handleInputChange("is_custom_site", v === "other");
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select country..." />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map(country => (
                              <SelectItem key={country} value={country}>{country}</SelectItem>
                            ))}
                            <SelectItem value="other">Other / Custom Site</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {formData.country && formData.country !== "other" && (
                        <div className="space-y-2">
                          <Label>Dive Site</Label>
                          <Select 
                            value={formData.dive_site_id}
                            onValueChange={(v) => {
                              handleInputChange("dive_site_id", v);
                              handleInputChange("is_custom_site", v === "other");
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select dive site..." />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredDiveSites.map(site => (
                                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                              ))}
                              <SelectItem value="other">Other / New Site</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Custom Site Input */}
                    {(formData.is_custom_site || formData.dive_site_id === "other") && (
                      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                        <div className="space-y-2">
                          <Label>Custom Site Name</Label>
                          <Input
                            value={formData.custom_site_name}
                            onChange={(e) => handleInputChange("custom_site_name", e.target.value)}
                            placeholder="Enter dive site name"
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Latitude (optional)</Label>
                            <Input
                              type="number"
                              step="any"
                              value={formData.custom_coordinates.lat}
                              onChange={(e) => handleNestedChange("custom_coordinates", "lat", e.target.value)}
                              placeholder="e.g., 9.7489"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Longitude (optional)</Label>
                            <Input
                              type="number"
                              step="any"
                              value={formData.custom_coordinates.lng}
                              onChange={(e) => handleNestedChange("custom_coordinates", "lng", e.target.value)}
                              placeholder="e.g., 123.8658"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Conditions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Waves className="w-5 h-5 text-cyan-600" />
                      Conditions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Current</Label>
                        <Select 
                          value={formData.conditions.current}
                          onValueChange={(v) => handleNestedChange("conditions", "current", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENT_OPTIONS.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Waves</Label>
                        <Select 
                          value={formData.conditions.waves}
                          onValueChange={(v) => handleNestedChange("conditions", "waves", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {WAVE_OPTIONS.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Surge</Label>
                        <Select 
                          value={formData.conditions.surge}
                          onValueChange={(v) => handleNestedChange("conditions", "surge", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {SURGE_OPTIONS.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Visibility</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            step="0.5"
                            value={formData.conditions.visibility}
                            onChange={(e) => handleNestedChange("conditions", "visibility", e.target.value)}
                            placeholder="0"
                            className="flex-1"
                          />
                          <Select value={visibilityUnit} onValueChange={setVisibilityUnit}>
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="m">m</SelectItem>
                              <SelectItem value="ft">ft</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Marine Life Observations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Fish className="w-5 h-5 text-cyan-600" />
                      Marine Life Observations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="relative">
                      <Input
                        value={marineLifeInput}
                        onChange={(e) => {
                          setMarineLifeInput(e.target.value);
                          setShowSuggestions(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (marineLifeInput.trim()) {
                              addMarineLifeObservation(marineLifeInput.trim());
                            }
                          }
                        }}
                        placeholder="Type species name and press Enter..."
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 300)}
                      />
                      
                      {/* Suggestions dropdown - Combined from dive sites and marine database */}
                      {showSuggestions && (() => {
                        // When no input, show all popular site species; when typing, show filtered
                        const visibleSiteSuggestions = marineLifeInput.length >= 2
                          ? diveSiteSuggestions
                          : allSiteSpecies.map(s => s.name);
                        // Filter out already-added observations
                        const filteredVisible = visibleSiteSuggestions.filter(
                          name => !formData.marine_life_observations.includes(name)
                        );
                        const showDropdown = filteredVisible.length > 0 || databaseSuggestions.length > 0;
                        
                        return showDropdown ? (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {/* Dive Site Observations - Cyan - with Thumbnails + Preview */}
                          {filteredVisible.length > 0 && (
                            <>
                              <div className="px-3 py-1 bg-cyan-50 text-xs font-semibold text-cyan-700 sticky top-0">
                                {marineLifeInput.length < 2 ? "Popular at this site" : "Previously observed at this site"}
                              </div>
                              {filteredVisible.map((suggestion, idx) => {
                                const thumbData = siteSuggestionThumbnails[suggestion];
                                const freq = allSiteSpecies.find(s => s.name === suggestion)?.frequency;
                                return (
                                  <div
                                    key={`site-${idx}`}
                                    className="flex items-center gap-3 px-4 py-2 hover:bg-cyan-50 transition-colors group"
                                  >
                                    <button
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        addMarineLifeObservation(suggestion);
                                      }}
                                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                    >
                                      {thumbData?.thumbnail ? (
                                        <img 
                                          src={thumbData.thumbnail.startsWith('http') ? thumbData.thumbnail : `${BACKEND_URL}${thumbData.thumbnail}`}
                                          alt=""
                                          className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                                          <Fish className="w-4 h-4 text-white" />
                                        </div>
                                      )}
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-cyan-700 truncate">{suggestion}</div>
                                        <div className="text-xs text-gray-500 italic truncate">
                                          {thumbData?.scientific_name || ""}
                                          {freq > 1 && <span className="ml-1 not-italic text-cyan-500">({freq} sightings)</span>}
                                        </div>
                                      </div>
                                    </button>
                                    <button
                                      type="button"
                                      onMouseDown={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        try {
                                          const res = await axios.get(`${API}/marine-database/search?q=${encodeURIComponent(suggestion)}`);
                                          const match = res.data?.find(s => s.common_names?.some(n => n.toLowerCase() === suggestion.toLowerCase()));
                                          if (match) {
                                            setPreviewSpecies(match);
                                          } else if (res.data?.[0]) {
                                            setPreviewSpecies(res.data[0]);
                                          } else {
                                            toast.info("No species card found in the database");
                                          }
                                        } catch {
                                          toast.error("Could not load species info");
                                        }
                                      }}
                                      className="p-1.5 rounded-md text-gray-400 hover:text-cyan-600 hover:bg-cyan-100 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                                      title="Preview species"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          
                          {/* Marine Database - with Thumbnails + Preview */}
                          {databaseSuggestions.length > 0 && (
                            <>
                              <div className="px-3 py-1 bg-violet-50 text-xs font-semibold text-violet-700 sticky top-0 border-t">
                                From Marine Database
                              </div>
                              {databaseSuggestions.map((suggestion, idx) => (
                                <div
                                  key={`db-${idx}`}
                                  className="flex items-center gap-3 px-4 py-2 hover:bg-violet-50 transition-colors group"
                                >
                                  <button
                                    type="button"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      addMarineLifeObservation(suggestion.name);
                                    }}
                                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                  >
                                    {suggestion.thumbnail ? (
                                      <img 
                                        src={suggestion.thumbnail.startsWith('http') ? suggestion.thumbnail : `${BACKEND_URL}${suggestion.thumbnail}`}
                                        alt=""
                                        className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                                        <Fish className="w-4 h-4 text-white" />
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-violet-700 truncate">{suggestion.name}</div>
                                      <div className="text-xs text-gray-500 italic truncate">{suggestion.scientific_name}</div>
                                    </div>
                                  </button>
                                  <button
                                    type="button"
                                    onMouseDown={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      try {
                                        const res = await axios.get(`${API}/marine-database/${suggestion.id}`);
                                        setPreviewSpecies(res.data);
                                      } catch {
                                        toast.error("Could not load species info");
                                      }
                                    }}
                                    className="p-1.5 rounded-md text-gray-400 hover:text-violet-600 hover:bg-violet-100 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                                    title="Preview species"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                        ) : null;
                      })()}
                    </div>
                    
                    {/* Selected observations */}
                    {formData.marine_life_observations.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.marine_life_observations.map((obs, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
                          >
                            {obs}
                            <button
                              type="button"
                              onClick={() => removeMarineLifeObservation(obs)}
                              className="hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Rating */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-cyan-600" />
                      Site Rating
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleRatingChange(star)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-10 h-10 ${
                              star <= formData.rating
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300 hover:text-yellow-300"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Site Photos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Camera className="w-5 h-5 text-cyan-600" />
                      Site Photos
                    </CardTitle>
                    <CardDescription>
                      Photos will be added to the community gallery unless marked as private
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Upload Photos</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleSitePhotoUpload}
                        className="hidden"
                      />
                    </label>
                    
                    {formData.site_photos.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {formData.site_photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={`${BACKEND_URL}${photo.url}`}
                              alt={`Site photo ${idx + 1}`}
                              className={`w-full h-32 object-cover rounded-lg ${photo.hide_from_gallery ? 'opacity-50' : ''}`}
                            />
                            <div className="absolute top-2 right-2 flex gap-1">
                              <button
                                type="button"
                                onClick={() => removeSitePhoto(idx)}
                                className="bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="absolute bottom-2 left-2 right-2">
                              <label className="flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded px-2 py-1 text-white text-xs">
                                <Checkbox
                                  checked={photo.hide_from_gallery}
                                  onCheckedChange={() => togglePhotoGalleryVisibility(idx)}
                                />
                                Hide from gallery
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Species ID */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Fish className="w-5 h-5 text-cyan-600" />
                      Species Identification
                    </CardTitle>
                    <CardDescription>
                      Request identification for unknown species in your photos
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formData.species_id_requested}
                        onCheckedChange={(v) => handleInputChange("species_id_requested", v)}
                      />
                      <Label>Request Species ID</Label>
                    </div>
                    
                    {formData.species_id_requested && formData.site_photos.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600">
                          Select photos for identification:
                        </Label>
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                          {formData.site_photos.map((photo, idx) => (
                            <div 
                              key={idx}
                              onClick={() => toggleSpeciesIdPhoto(photo.url)}
                              className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                formData.species_id_photo_urls.includes(photo.url)
                                  ? 'border-cyan-500 ring-2 ring-cyan-200'
                                  : 'border-transparent hover:border-gray-300'
                              }`}
                            >
                              <img 
                                src={`${BACKEND_URL}${photo.url}`}
                                alt={`Photo ${idx + 1}`}
                                className="w-full h-24 object-cover"
                              />
                              {formData.species_id_photo_urls.includes(photo.url) && (
                                <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                                  <div className="bg-cyan-500 text-white rounded-full p-1">
                                    <Fish className="w-4 h-4" />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          {formData.species_id_photo_urls.length} photo(s) selected for identification
                        </p>
                      </div>
                    )}
                    
                    {formData.species_id_requested && formData.site_photos.length === 0 && (
                      <p className="text-sm text-amber-600">
                        Upload site photos first to select them for species identification
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Navigation to Notes */}
                <div className="flex justify-end gap-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setActiveTab("dive")}
                  >
                    Back to Dive Info
                  </Button>
                  <Button 
                    type="button"
                    onClick={() => setActiveTab("notes")}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Continue to Notes
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB 3: NOTES */}
              <TabsContent value="notes" className="space-y-6">
                {/* Dive Center */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-cyan-600" />
                      Dive Center
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      <Input
                        value={diveCenterInput}
                        onChange={(e) => {
                          setDiveCenterInput(e.target.value);
                          setShowDiveCenterSuggestions(true);
                          // Clear selection if user is typing
                          if (formData.dive_center_name !== e.target.value) {
                            setFormData(prev => ({ ...prev, dive_center_id: "", dive_center_name: "" }));
                          }
                        }}
                        placeholder="Search for a dive center..."
                        onFocus={() => setShowDiveCenterSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowDiveCenterSuggestions(false), 300)}
                      />
                      
                      {/* Dive center suggestions */}
                      {showDiveCenterSuggestions && diveCenterSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {diveCenterSuggestions.map((dc) => (
                            <button
                              key={dc.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectDiveCenter(dc);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-cyan-50 transition-colors"
                            >
                              <div className="font-medium">{dc.name}</div>
                              {dc.location && (
                                <div className="text-xs text-gray-500">{dc.location}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {formData.dive_center_name && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm text-green-600">✓ Selected:</span>
                        <span className="text-sm font-medium">{formData.dive_center_name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Buddy */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-cyan-600" />
                      Dive Buddy
                    </CardTitle>
                    <CardDescription>
                      Add members you dove with
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="relative">
                      <Input
                        value={buddyInput}
                        onChange={(e) => {
                          setBuddyInput(e.target.value);
                          setShowBuddySuggestions(true);
                        }}
                        placeholder="Search for members..."
                        onFocus={() => setShowBuddySuggestions(true)}
                        onBlur={() => setTimeout(() => setShowBuddySuggestions(false), 300)}
                      />
                      
                      {/* Buddy suggestions */}
                      {showBuddySuggestions && buddySuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {buddySuggestions.map((member) => (
                            <button
                              key={member.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                addBuddy(member);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-cyan-50 transition-colors flex items-center gap-3"
                            >
                              {member.profile_picture ? (
                                <img 
                                  src={getImageUrl(member.profile_picture)}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="w-4 h-4 text-gray-500" />
                                </div>
                              )}
                              <div>
                                <div className="font-medium">{member.name || member.username}</div>
                                {member.name && member.username && (
                                  <div className="text-xs text-gray-500">@{member.username}</div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Selected buddies */}
                    {formData.buddies.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.buddies.map((buddy) => (
                          <span 
                            key={buddy.user_id}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
                          >
                            {buddy.user_name}
                            <button
                              type="button"
                              onClick={() => removeBuddy(buddy.user_id)}
                              className="hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                      Notes
                    </CardTitle>
                    <CardDescription>
                      Any additional notes about your dive
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange("notes", e.target.value)}
                      placeholder="Write your dive notes here..."
                      className="min-h-[200px]"
                    />
                  </CardContent>
                </Card>

                {/* Submit */}
                <div className="flex justify-end gap-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setActiveTab("site")}
                  >
                    Back to Site Info
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 px-8"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Dive Report"
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </form>
        </div>
      </section>

      <FloatingUserMenu />
      <Footer />

      {previewSpecies && (
        <SpeciesDetailModal
          species={previewSpecies}
          onClose={() => setPreviewSpecies(null)}
          canEdit={false}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default DiveReportForm;
